import React from "react";
import '../../App.scss';

const DEFAULT_STEPS = ["Confirmed", "On the way", "Delivered"];
const STOP_LIMIT = 10;

/**
 * Props:
 * - step: number (0..3)
 * - steps?: string[]
 * - variant?: "normal" | "issue"
 * - issueLabel?: string (e.g. "Attempt failed")
 * - issueText?: string (short helper text)
 */
export function DeliveryStatusBar({
    step,
    steps = DEFAULT_STEPS,
    variant = "normal",
    issueLabel,
    issueText,
    terminal = false,
    stopsAhead = null,
}) {
    const clampedStep = Math.max(0, Math.min(step, steps.length - 1));

    /**
     * Render the remaining number of stops before this delivery text. Will only show text if it's 10 stops left or less
     * @param {number} stopsAhead - Number of stops remaining before delivery
     * @return {string|null} - Number of stops left or null if more than 10 stops away
     */
    const renderStopsText = (stopsAhead) => {
        if (typeof stopsAhead !== "number" || stopsAhead < 0) return null;

        if (stopsAhead === 0) return "You’re the next stop";
        if (stopsAhead <= STOP_LIMIT) return `${stopsAhead} ${stopsAhead === 1 ? "stop" : "stops"} away`;
        return null;
    };

    return (
        <section className="StatusBar">
            <div className="StatusBar__top">
                {variant === "issue" && issueLabel ? (
                    <span className="StatusBar__chip">
            {issueLabel}
          </span>
                ) : null}
            </div>
            <ol className="StatusBar__track" style={{ "--steps": steps.length }}>
                {steps.map((label, i) => {
                    const isComplete = clampedStep === steps.length - 1;

                    // ✅ If complete, everything up to and including last dot is "done"
                    const isDone = (isComplete || terminal) ? i <= clampedStep : i < clampedStep;

                    // ✅ If complete, there is no "active" (in-progress) step
                    const isActive = !isComplete && !terminal && i === clampedStep;

                    // connector line should be done when the step AFTER it is done
                    const isLineDone = i < clampedStep;

                    return (
                        <li
                            key={label}
                            className={[
                                "StatusBar__step",
                                isDone ? "is-done" : "",
                                isActive ? "is-active" : "",
                                variant === "issue" ? "is-issue" : "",
                            ].join(" ")}
                        >
                            <div className="StatusBar__dot" aria-hidden="true" />
                            <div className="StatusBar__label">{label}</div>

                            {i < steps.length - 1 ? (
                                <div
                                    className={[
                                        "StatusBar__line",
                                        isLineDone ? "is-line-done" : "",
                                    ].join(" ")}
                                    aria-hidden="true"
                                />
                            ) : null}
                        </li>
                    );
                })}
            </ol>
            {typeof stopsAhead === "number" && stopsAhead <= STOP_LIMIT && !terminal ? (
                <div className="StatusBar__sub">
                    {renderStopsText(stopsAhead)}
                </div>
            ) : null}
            {variant === "issue" && issueText ? (
                <div className="StatusBar__help">{issueText}</div>
            ) : null}
        </section>
    );
}
